import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ShieldCheck, Clock, CreditCard, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PromoBanner } from "../components/PublicBanners";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const FAQ = [
  {
    q: "Who is Freddy Compute Academy for?",
    a: "Any role inside an automotive dealership that wants to use AI as a daily lever — Marketing, Sales, Service, Operations, and the Executive seat. Programs are built for operators, not theorists.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Cancel anytime from your dashboard. You keep access until the end of the billed period and there's no auto-renewal trap.",
  },
  {
    q: "What's the difference between Staff and Executive?",
    a: "Staff Membership unlocks every course in Marketing, Sales, Service, and Operations. Executive Membership adds the Executive Mindset track — strategy, capital allocation, AI governance — plus quarterly briefings and a 1:1 onboarding call.",
  },
  {
    q: "Do you offer group / dealership pricing?",
    a: "Yes — but that's launching in a later phase. For now, individual memberships are available and Executive members receive group reporting tools when they go live.",
  },
];

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busyPlan, setBusyPlan] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled") === "1";
  // Phase 11 — audience-aware pricing.
  // URL `?audience=` wins (anon visitors from a vertical landing), else the
  // logged-in user's audience, else fall back to "all".
  const audienceFromUrl = searchParams.get("audience");
  const effectiveAudience = audienceFromUrl || user?.audience || null;

  useEffect(() => {
    const params = effectiveAudience ? { audience: effectiveAudience } : {};
    api
      .get("/plans", { params })
      .then((r) => setPlans(r.data))
      .catch((e) => console.error("Failed to load plans", e));
  }, [effectiveAudience]);

  const choosePlan = async (plan) => {
    if (!user) {
      navigate(`/register?plan=${plan.slug}`);
      return;
    }
    setBusyPlan(plan.slug);
    try {
      const r = await api.post("/checkout/session", { plan_id: plan.id });
      window.location.href = r.data.url;
    } catch (e) {
      console.error("Checkout failed", e);
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setBusyPlan(null);
    }
  };

  return (
    <div className="bg-fc-navy min-h-screen text-white">
      <Navbar promoLocation="pricing" />
      <main className="pt-32 pb-24" data-testid="pricing-page">
        {cancelled && (
          <div className="max-w-6xl mx-auto px-6 lg:px-10 mb-8">
            <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#F59E0B] p-4 flex items-start gap-3" data-testid="checkout-cancelled-banner">
              <AlertCircle className="flex-shrink-0 mt-0.5" size={18} />
              <div>
                <p className="font-bold">Checkout cancelled</p>
                <p className="text-sm opacity-80">No charge was made. Pick a plan whenever you're ready.</p>
              </div>
            </div>
          </div>
        )}
        <section className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-16">
            <div className="fc-overline mb-3">PRICING</div>
            <h1 className="fc-h-section text-4xl sm:text-5xl lg:text-6xl mb-6">
              Built for <span className="text-fc-red">real dealerships</span>.
            </h1>
            <p className="text-white/70 max-w-2xl mx-auto text-lg leading-relaxed">
              Annual memberships. Cancel anytime. Every program is operator-led and
              built around launching results inside 30 days.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {plans.map((p) => (
              <div
                key={p.id}
                className={`relative bg-[#060A24] p-8 lg:p-10 flex flex-col ${
                  p.is_recommended ? "border-2 border-fc-red shadow-[0_24px_48px_-16px_rgba(255,67,67,0.25)]" : "border border-white/10"
                }`}
                data-testid={`pricing-card-${p.slug}`}
              >
                {p.is_recommended && (
                  <div className="absolute top-0 right-0 bg-fc-red text-white text-[10px] tracking-[0.22em] font-bold px-4 py-2" data-testid="most-popular-ribbon">
                    MOST POPULAR
                  </div>
                )}
                <p className="text-fc-red text-xs tracking-[0.22em] font-bold uppercase mb-3">
                  {p.name}
                </p>
                <p className="text-white/60 text-sm leading-relaxed mb-6">{p.description}</p>
                <div className="flex items-end gap-2 mb-1">
                  <span className="text-6xl font-black tracking-tight">{p.price_display}</span>
                  <span className="text-white/50 text-sm pb-3">/year</span>
                </div>
                <p className="text-white/50 text-sm mb-8">
                  ≈ {p.monthly_equivalent} / month, billed annually
                </p>

                <ul className="space-y-3 mb-10 flex-1">
                  {p.features?.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-white/85" data-testid={`pricing-${p.slug}-feature-${i}`}>
                      <Check className="text-[#10B981] flex-shrink-0 mt-0.5" size={16} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/register?plan=${p.slug}`}
                  onClick={(e) => { e.preventDefault(); choosePlan(p); }}
                  className={p.is_recommended ? "btn-fc-red w-full justify-center" : "btn-fc-outline w-full justify-center"}
                  data-testid={`pricing-${p.slug}-cta`}
                >
                  {busyPlan === p.slug ? "Redirecting…" : "Choose Plan"}
                </Link>
              </div>
            ))}
          </div>

          {/* TRUST ROW */}
          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-white/60 text-xs sm:text-sm tracking-wider">
            <span className="flex items-center gap-2"><Clock size={14} className="text-fc-red" /> Cancel anytime</span>
            <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-fc-red" /> Secure checkout</span>
            <span className="flex items-center gap-2"><CreditCard size={14} className="text-fc-red" /> 7-day money-back</span>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-6 lg:px-10 mt-24" data-testid="pricing-faq">
          <div className="mb-10 text-center">
            <div className="fc-overline mb-3">FAQ</div>
            <h2 className="fc-h-section text-3xl sm:text-4xl">Common questions.</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-[#060A24] border border-white/10 px-6"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="text-left text-white font-bold hover:no-underline hover:text-fc-red">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-white/65 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </main>
      <Footer />
    </div>
  );
}
